import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import { ApiError } from "@njbsoft/api-client";
import { adminApiClient } from "@/lib/auth";
import { adminKeys } from "@/hooks/use-admin-keys";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";

type AdminCategoricalStandard = AdminComponents["schemas"]["AdminCategoricalStandard"];

const PAGE_SIZE = 20;

export const Route = createFileRoute("/_admin/categorical-standards/")({
  component: CategoricalStandardsPage,
});

const colHelper = createColumnHelper<AdminCategoricalStandard>();

function limitBasisLabel(basis: string): string {
  switch (basis) {
    case "concentration":
      return "Concentration";
    case "mass_per_production":
      return "Mass/Production";
    case "mass_per_day":
      return "Mass/Day";
    default:
      return basis;
  }
}

function formatLimitValue(
  value: number | null | undefined,
  unit: string | null | undefined,
): string {
  if (value === null || value === undefined) return "-";
  return unit ? `${value} ${unit}` : String(value);
}

function CategoricalStandardsPage() {
  const [search, setSearch] = useState("");
  const [cfrPart, setCfrPart] = useState("");
  const [techBasis, setTechBasis] = useState("");
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data, isLoading } = useQuery({
    queryKey: adminKeys.categoricalStandards({
      search,
      cfr_part: cfrPart,
      technology_basis: techBasis,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    queryFn: async () => {
      const {
        data: resp,
        error,
        response,
      } = await adminApiClient.GET("/categorical-standards", {
        params: {
          query: {
            search: search || undefined,
            cfr_part: cfrPart ? Number(cfrPart) : undefined,
            technology_basis: techBasis || undefined,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
          },
        },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to fetch categorical standards";
        throw new ApiError(response.status, msg);
      }
      return resp;
    },
    staleTime: 60_000,
  });

  const columns = useMemo(
    () => [
      colHelper.accessor("cfr_part", {
        header: "CFR Part",
        enableSorting: false,
        cell: (info) => <span className="font-mono text-sm">{info.getValue()}</span>,
      }),
      colHelper.accessor("point_source_category", {
        header: "Point Source Category",
        enableSorting: false,
      }),
      colHelper.accessor("subcategory", {
        header: "Subcategory",
        enableSorting: false,
        cell: (info) => <span className="text-sm text-slate-500">{info.getValue() ?? "-"}</span>,
      }),
      colHelper.accessor("technology_basis", {
        header: "Technology Basis",
        enableSorting: false,
        cell: (info) => (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            {info.getValue()}
          </span>
        ),
      }),
      colHelper.accessor("limit_basis", {
        header: "Limit Basis",
        enableSorting: false,
        cell: (info) => (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
            {limitBasisLabel(info.getValue())}
          </span>
        ),
      }),
      colHelper.accessor("parameter_name", {
        header: "Parameter",
        enableSorting: false,
      }),
      colHelper.display({
        id: "daily_maximum",
        header: "Daily Maximum",
        cell: (info) =>
          formatLimitValue(
            info.row.original.daily_maximum_value,
            info.row.original.daily_maximum_unit,
          ),
      }),
      colHelper.display({
        id: "monthly_average",
        header: "Monthly Average",
        cell: (info) =>
          formatLimitValue(
            info.row.original.monthly_average_value,
            info.row.original.monthly_average_unit,
          ),
      }),
      colHelper.accessor("effective_date", {
        header: "Effective Date",
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue();
          return val ? new Date(val).toLocaleDateString() : "-";
        },
      }),
      colHelper.accessor("federal_register_citation", {
        header: "Fed. Register Citation",
        enableSorting: false,
        cell: (info) => <span className="text-sm text-slate-500">{info.getValue() ?? "-"}</span>,
      }),
    ],
    [],
  );

  const cfrParts = useMemo(() => {
    const items = data?.data ?? [];
    const unique = new Set(items.map((s) => s.cfr_part));
    return Array.from(unique).sort((a, b) => a - b);
  }, [data]);

  const techBases = useMemo(() => {
    const items = data?.data ?? [];
    const unique = new Set(items.map((s) => s.technology_basis));
    return Array.from(unique).sort();
  }, [data]);

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Categorical Standards</h1>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search standards..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-64 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400"
        />
        <select
          value={cfrPart}
          onChange={(e) => {
            setCfrPart(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
        >
          <option value="">All CFR Parts</option>
          {cfrParts.map((p) => (
            <option key={p} value={String(p)}>
              Part {p}
            </option>
          ))}
        </select>
        <select
          value={techBasis}
          onChange={(e) => {
            setTechBasis(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
        >
          <option value="">All Technology Bases</option>
          {techBases.map((tb) => (
            <option key={tb} value={tb}>
              {tb}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        emptyMessage="No categorical standards found"
      />
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
